import { emptyUpsRateBreakdown, UpsRateBreakdown, UpsRateCharge, UpsServiceRate } from '../models/ups-rate-breakdown.model';
import { upsServiceLabel } from '../config/ups-service.config';

// Best-effort labels for common UPS itemized charge codes. Amounts are always exact;
// labels fall back to the code when unknown, so nothing is ever mislabeled silently.
const UPS_CHARGE_CODE_LABELS: Record<string, string> = {
    '100': 'Transportation',
    '110': 'Fuel Surcharge',
    '120': 'Additional Handling',
    '190': 'Delivery Area Surcharge',
    '258': 'Large Package Surcharge',
    '270': 'Fuel Surcharge',
    '271': 'Carbon Neutral',
    '300': 'Residential Surcharge',
    '375': 'Residential Surcharge',
    '376': 'Delivery Area Surcharge',
    '400': 'Signature Required',
    '435': 'Peak / Demand Surcharge',
};

function chargeLabel(c: any): string {
    if (c?.Description) return String(c.Description);
    const code = c?.Code != null ? String(c.Code) : '';
    if (code && UPS_CHARGE_CODE_LABELS[code]) return UPS_CHARGE_CODE_LABELS[code];
    return code ? `Charge ${code}` : 'Charge';
}

function toChargeArray(x: any): UpsRateCharge[] {
    if (!x) return [];
    const arr = Array.isArray(x) ? x : [x];
    return arr
        .filter(c => c && c.MonetaryValue != null)
        .map(c => ({ code: c?.Code != null ? String(c.Code) : '', label: chargeLabel(c), amount: String(c.MonetaryValue) }));
}

// Pull a breakdown from a charges node (a RatedShipment / ShipmentResults for published,
// or a NegotiatedRateCharges object for negotiated).
function extractBreakdown(node: any): UpsRateBreakdown {
    if (!node || typeof node !== 'object') return emptyUpsRateBreakdown();
    // RatedShipment/ShipmentCharges use TotalCharges; NegotiatedRateCharges uses TotalCharge.
    const total = node.TotalCharges ?? node.TotalCharge ?? node.ShipmentCharges?.TotalCharges;
    const charges = node.ShipmentCharges ?? node;
    const billing = node.BillingWeight;
    return {
        currency: total?.CurrencyCode ?? charges?.TransportationCharges?.CurrencyCode ?? '',
        transportation: charges?.TransportationCharges?.MonetaryValue != null ? String(charges.TransportationCharges.MonetaryValue) : '',
        serviceOptions: charges?.ServiceOptionsCharges?.MonetaryValue != null ? String(charges.ServiceOptionsCharges.MonetaryValue) : '',
        itemized: toChargeArray(node.ItemizedCharges ?? charges?.ItemizedCharges),
        taxes: toChargeArray(node.TaxCharges ?? charges?.TaxCharges),
        total: total?.MonetaryValue != null ? String(total.MonetaryValue) : '',
        billingWeight: billing ? `${billing.Weight ?? ''} ${billing.UnitOfMeasurement?.Code ?? ''}`.trim() : '',
    };
}

// Locate the rated node in either a RateResponse or a ShipmentResponse.
function findRatedNode(data: any): any {
    const rated = data?.RateResponse?.RatedShipment ?? data?.RatedShipment;
    if (rated) return Array.isArray(rated) ? rated[0] : rated;
    const results = data?.ShipmentResponse?.ShipmentResults ?? data?.ShipmentResults;
    if (results) return results;
    return data;
}

/**
 * Parse a UPS rate/shipment response into published + negotiated itemized breakdowns.
 * `negotiated` is null when the account/response carries no negotiated pricing.
 */
export function parseUpsRateBreakdowns(data: unknown): { published: UpsRateBreakdown; negotiated: UpsRateBreakdown | null } {
    const node = findRatedNode(data);
    const published = extractBreakdown(node);
    const negNode = node?.NegotiatedRateCharges ?? node?.ShipmentCharges?.NegotiatedRateCharges;
    const negotiated = negNode ? extractBreakdown(negNode) : null;
    return { published, negotiated };
}

// Pull the estimated delivery date (ISO yyyy-mm-dd) + business days from a RatedShipment's
// time-in-transit / guaranteed-delivery block. Returns '' when not present.
function extractTransit(rated: any): { deliveryDate: string; businessDays: string } {
    const arrival = rated?.TimeInTransit?.ServiceSummary?.EstimatedArrival;
    // UPS gives the date as yyyymmdd; normalize to yyyy-mm-dd.
    const raw = arrival?.Arrival?.Date ?? '';
    const deliveryDate = /^\d{8}$/.test(raw) ? `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}` : '';
    const businessDays = String(arrival?.BusinessDaysInTransit ?? rated?.GuaranteedDelivery?.BusinessDaysInTransit ?? '');
    return { deliveryDate, businessDays };
}

/**
 * Parse a UPS "Shop" rate response (rates for all services) into one UpsServiceRate per
 * service, each with published/negotiated breakdowns and an estimated delivery date.
 */
export function parseUpsShopRates(data: unknown): UpsServiceRate[] {
    const rated = (data as any)?.RateResponse?.RatedShipment ?? (data as any)?.RatedShipment;
    if (!rated) return [];
    const list = Array.isArray(rated) ? rated : [rated];
    return list.map((node: any) => {
        const negNode = node?.NegotiatedRateCharges ?? node?.ShipmentCharges?.NegotiatedRateCharges;
        const code = String(node?.Service?.Code ?? '');
        const { deliveryDate, businessDays } = extractTransit(node);
        return {
            serviceCode: code,
            serviceLabel: upsServiceLabel(code),
            published: extractBreakdown(node),
            negotiated: negNode ? extractBreakdown(negNode) : null,
            deliveryDate,
            businessDays,
        };
    }).filter((s: UpsServiceRate) => !!s.serviceCode);
}
